import React, { useState, useEffect } from 'react';
import './App.css';
import TemplateList from './components/TemplateList';
import CardEditor from './components/CardEditor';
import PropertyPanel from './components/PropertyPanel';

const systemTemplates = [
  { key: 'classic', name: '经典', color: 'linear-gradient(128deg, #185491 0%, #a72f6d 100%)', cardWidth: 557, contentBgOpacity: 53, textColor: '#ffffff' },
  { key: 'photo', name: '画卷', color: '#e1d3c8', cardWidth: 800, contentBgOpacity: 0 },
  { key: 'rainbow', name: '彩虹', color: 'linear-gradient(90deg, #ff5e62 0%, #ff9966 25%, #f9d423 50%, #a8ff78 75%, #43cea2 100%)', textColor: '#fcfcfc' },
  { key: 'neon', name: '霓虹', color: 'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)', textColor: '#fcfcfc' },
  { key: 'aurora', name: '极光', color: 'linear-gradient(135deg, #00c3ff 0%, #ffff1c 100%)', textColor: '#fcfcfc' },
  { key: 'fire', name: '火焰', color: 'linear-gradient(135deg, #ff512f 0%, #dd2476 100%)', textColor: '#fcfcfc' },
  { key: 'ice', name: '冰蓝', color: 'linear-gradient(135deg, #83a4d4 0%, #b6fbff 100%)', textColor: '#fcfcfc' },
  { key: 'forest', name: '绿野', color: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', textColor: '#fcfcfc' },
  { key: 'night', name: '暗夜', color: 'linear-gradient(135deg, #434343 0%, #000000 100%)', textColor: '#f5f5f5' }
];

const systemTemplateKeys = [
  'classic', 'photo', 'rainbow', 'neon', 'aurora', 'fire', 'ice', 'forest',
  'night'
];

const allSystemTemplateKeys = systemTemplates.map(t => t.key);

const getTemplateDefaults = (key) => {
  const template = systemTemplates.find(t => t.key === key);
  return {
    cardWidth: template?.cardWidth ?? 557,
    contentBgOpacity: template?.contentBgOpacity ?? 53,
    textColor: template?.textColor ?? '#050505'
  };
};

const backgroundImages = [
  { key: 'bg-001', name: '001', url: `${process.env.PUBLIC_URL}/backgrounds/001.png` },
  { key: 'bg-002', name: '002', url: `${process.env.PUBLIC_URL}/backgrounds/002.png` },
  { key: 'bg-003', name: '003', url: `${process.env.PUBLIC_URL}/backgrounds/003.png` },
  { key: 'bg-004', name: '004', url: `${process.env.PUBLIC_URL}/backgrounds/004.png` },
  { key: 'bg-005', name: '005', url: `${process.env.PUBLIC_URL}/backgrounds/005.png` },
  { key: 'bg-006', name: '006', url: `${process.env.PUBLIC_URL}/backgrounds/006.png` },
  { key: 'bg-007', name: '007', url: `${process.env.PUBLIC_URL}/backgrounds/007.png` },
  { key: 'bg-008', name: '008', url: `${process.env.PUBLIC_URL}/backgrounds/008.png` },
  { key: 'bg-009', name: '009', url: `${process.env.PUBLIC_URL}/backgrounds/009.png` },
  { key: 'bg-010', name: '010', url: `${process.env.PUBLIC_URL}/backgrounds/010.png` },
  { key: 'bg-011', name: '011', url: `${process.env.PUBLIC_URL}/backgrounds/011.png` },
  { key: 'bg-012', name: '012', url: `${process.env.PUBLIC_URL}/backgrounds/012.png` },
  { key: 'bg-013', name: '013', url: `${process.env.PUBLIC_URL}/backgrounds/013.png`, textColor: '#fcfcfc' },
  { key: 'bg-014', name: '014', url: `${process.env.PUBLIC_URL}/backgrounds/014.png`, textColor: '#fcfcfc' },
  { key: 'bg-015', name: '015', url: `${process.env.PUBLIC_URL}/backgrounds/015.png` },
  { key: 'bg-016', name: '016', url: `${process.env.PUBLIC_URL}/backgrounds/016.png` },
  { key: 'bg-017', name: '017', url: `${process.env.PUBLIC_URL}/backgrounds/017.png` },
  { key: 'bg-018', name: '018', url: `${process.env.PUBLIC_URL}/backgrounds/018.png` },
  { key: 'bg-019', name: '019', url: `${process.env.PUBLIC_URL}/backgrounds/019.png` },
  { key: 'bg-020', name: '020', url: `${process.env.PUBLIC_URL}/backgrounds/020.png` },
  { key: 'bg-021', name: '021', url: `${process.env.PUBLIC_URL}/backgrounds/021.png` }
];

const fontOptions = [
  { label: '微软雅黑', value: "'Microsoft YaHei', 'PingFang SC', sans-serif" },
  { label: '宋体', value: "'SimSun', 'Songti SC', serif" },
  { label: '楷体', value: "'KaiTi', 'STKaiti', serif" },
  { label: '黑体', value: "'Heiti SC', 'SimHei', sans-serif" },
  { label: '思源黑体', value: "'Noto Sans SC', 'Source Han Sans SC', sans-serif" },
  { label: '思源宋体', value: "'Noto Serif SC', 'Source Han Serif SC', serif" },
  { label: '站酷快乐体', value: "'ZCOOL KuaiLe', cursive" },
  { label: '站酷小薇体', value: "'ZCOOL XiaoWei', serif" },
  { label: '马善政毛笔体', value: "'Ma Shan Zheng', cursive" },
  { label: '龙藏体', value: "'Long Cang', cursive" },
  { label: '霞鹜文楷', value: "'LXGW WenKai', serif" }
];

function App() {
  const DEFAULTS_VERSION = '2024-09-20';
  const resolveInitialTemplateKey = () => {
    if (typeof window === 'undefined') return 'photo';
    const saved = localStorage.getItem('selectedTemplate');
    if (saved && allSystemTemplateKeys.includes(saved)) {
      return saved;
    }
    return 'photo';
  };
  const initialTemplateKey = resolveInitialTemplateKey();
  const initialTemplateDefaults = getTemplateDefaults(initialTemplateKey);
  const [selectedTemplate, setSelectedTemplate] = useState(initialTemplateKey);
  const [showDate, setShowDate] = useState(true);
  const [showAuthor, setShowAuthor] = useState(true);
  const [showCount, setShowCount] = useState(true);
  const [showAvatar, setShowAvatar] = useState(true);
  const [customBgType, setCustomBgType] = useState('template');
  const [customBgColor, setCustomBgColor] = useState('#3A8DDE');
  const [customBgGradient, setCustomBgGradient] = useState({
    from: '#3A8DDE',
    to: '#F357A8',
    angle: 135
  });
  const [textColor, setTextColor] = useState(() => {
    if (typeof window === 'undefined') return '#050505';
    const saved = localStorage.getItem('textColor');
    if (saved) return saved;
    const templateKey = resolveInitialTemplateKey();
    return getTemplateDefaults(templateKey).textColor;
  });
  const [textScale, setTextScale] = useState(() => {
    if (typeof window === 'undefined') return 1;
    const stored = Number(localStorage.getItem('textScale'));
    return Number.isFinite(stored) && stored > 0 ? stored : 1;
  });
  const [customBgImage, setCustomBgImage] = useState(() => {
    if (typeof window === 'undefined') {
      return `${process.env.PUBLIC_URL}/backgrounds/001.png`;
    }
    return localStorage.getItem('customBgImage') || `${process.env.PUBLIC_URL}/backgrounds/001.png`;
  });
  const [contentBgOpacity, setContentBgOpacity] = useState(initialTemplateDefaults.contentBgOpacity);
  const defaultFont = `'SimSun', 'Songti SC', serif`;
  const [fontFamily, setFontFamily] = useState(() => {
    if (typeof window === 'undefined') return defaultFont;
    const stored = localStorage.getItem('fontFamily');
    if (stored === "'Songti SC', 'SimSun', serif") {
      return defaultFont;
    }
    return stored || defaultFont;
  });
  const [customTemplates, setCustomTemplates] = useState(() => {
    const saved = localStorage.getItem('customTemplates');
    return saved ? JSON.parse(saved) : [];
  });
  const [editorResetKey, setEditorResetKey] = useState(0);
  const [cardWidth, setCardWidth] = useState(initialTemplateDefaults.cardWidth);
  const [avatar, setAvatar] = useState(() => {
    return localStorage.getItem('userAvatar') || null;
  });
  const [initialContent, setInitialContent] = useState('');

  const resetToDefaults = () => {
    const fallback = 'photo';
    const defaults = getTemplateDefaults(fallback);
    const resetKeys = [
      'selectedTemplate',
      'fontFamily',
      'textColor',
      'textScale',
      'customBgImage',
      'customTemplates',
      'userAvatar',
      'defaultsVersion'
    ];
    resetKeys.forEach((key) => localStorage.removeItem(key));
    setSelectedTemplate(fallback);
    setShowDate(true);
    setShowAuthor(true);
    setShowCount(true);
    setShowAvatar(true);
    setCustomBgType('template');
    setCustomBgColor('#3A8DDE');
    setCustomBgGradient({ from: '#3A8DDE', to: '#F357A8', angle: 135 });
    setTextColor(defaults.textColor || '#050505');
    setTextScale(1);
    setCustomBgImage(`${process.env.PUBLIC_URL}/backgrounds/001.png`);
    setContentBgOpacity(defaults.contentBgOpacity ?? 53);
    setFontFamily(defaultFont);
    setCustomTemplates([]);
    setCardWidth(defaults.cardWidth ?? 557);
    setAvatar(null);
    setInitialContent('');
    setEditorResetKey((prev) => prev + 1);
    if (typeof window !== 'undefined' && window.location.search) {
      window.history.replaceState({}, '', window.location.pathname);
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  useEffect(() => {
    localStorage.setItem('customTemplates', JSON.stringify(customTemplates));
  }, [customTemplates]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const storedVersion = localStorage.getItem('defaultsVersion');
    if (storedVersion === DEFAULTS_VERSION) return;
    const fallback = 'photo';
    const defaults = getTemplateDefaults(fallback);
    setSelectedTemplate(fallback);
    setCardWidth(defaults.cardWidth);
    setContentBgOpacity(defaults.contentBgOpacity);
    setTextColor(defaults.textColor);
    setFontFamily(defaultFont);
    localStorage.setItem('selectedTemplate', fallback);
    localStorage.setItem('fontFamily', defaultFont);
    localStorage.setItem('defaultsVersion', DEFAULTS_VERSION);
  }, [DEFAULTS_VERSION, defaultFont]);

  useEffect(() => {
    localStorage.setItem('selectedTemplate', selectedTemplate);
  }, [selectedTemplate]);

  useEffect(() => {
    if (fontFamily) {
      localStorage.setItem('fontFamily', fontFamily);
    } else {
      localStorage.removeItem('fontFamily');
    }
  }, [fontFamily]);

  useEffect(() => {
    if (textColor) {
      localStorage.setItem('textColor', textColor);
    }
  }, [textColor]);

  useEffect(() => {
    if (Number.isFinite(textScale) && textScale > 0) {
      localStorage.setItem('textScale', String(textScale));
    }
  }, [textScale]);

  useEffect(() => {
    if (customBgImage) {
      localStorage.setItem('customBgImage', customBgImage);
    }
  }, [customBgImage]);

  useEffect(() => {
    if (!selectedTemplate.startsWith('custom_')) {
      const defaults = getTemplateDefaults(selectedTemplate);
      if (defaults.textColor) {
        setTextColor(defaults.textColor);
      }
    }
  }, [selectedTemplate]);

  useEffect(() => {
    const allTemplateKeys = [
      ...systemTemplates.map(t => t.key),
      ...customTemplates.map(t => t.key)
    ];
    const saved = localStorage.getItem('selectedTemplate');
    if (!allTemplateKeys.includes(saved)) {
      const fallback = 'photo';
      localStorage.setItem('selectedTemplate', fallback);
      setSelectedTemplate(fallback);
      const defaults = getTemplateDefaults(fallback);
      setCardWidth(defaults.cardWidth);
      setContentBgOpacity(defaults.contentBgOpacity);
      setTextColor(defaults.textColor);
    }
  }, [customTemplates]);

  useEffect(() => {
    if (avatar) {
      localStorage.setItem('userAvatar', avatar);
    }
  }, [avatar]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tweetText = params.get('tweetText');
    if (tweetText) {
      setInitialContent(tweetText);
    }
  }, []);

  const handleDeleteCustomTemplate = (key) => {
    setCustomTemplates(prev => prev.filter(t => t.key !== key));
    if (selectedTemplate === key) {
      setSelectedTemplate('classic');
    }
  };

  const handleSaveTemplate = () => {
    let color = '';
    let name = `自定义${customTemplates.length + 1}`;
    if (customBgType === 'color') {
      color = customBgColor;
    } else if (customBgType === 'gradient') {
      color = `linear-gradient(${customBgGradient.angle}deg, ${customBgGradient.from} 0%, ${customBgGradient.to} 100%)`;
    } else {
      color = 'linear-gradient(135deg, #7b2ff2 0%, #f357a8 100%)';
    }
    const key = 'custom_' + Date.now();
    setCustomTemplates(prev => [...prev, { 
      key, 
      name, 
      color, 
      bgType: customBgType, 
      bgColor: customBgColor, 
      bgGradient: customBgGradient,
      cardWidth: cardWidth,
      contentBgOpacity: contentBgOpacity
    }]);
    setSelectedTemplate(key);
    setCustomBgType('template');
  };

  const allTemplates = [...systemTemplates, ...customTemplates];

  const handleSelectTemplate = (key) => {
    setSelectedTemplate(key);
    const found = customTemplates.find(t => t.key === key);
    if (found) {
      if (found.bgType === 'color') {
        setCustomBgType('color');
        setCustomBgColor(found.bgColor);
      } else if (found.bgType === 'gradient') {
        setCustomBgType('gradient');
        setCustomBgGradient(found.bgGradient);
      } else {
        setCustomBgType('template');
      }
      if (found.cardWidth) {
        setCardWidth(found.cardWidth);
      }
      if (found.contentBgOpacity !== undefined) {
        setContentBgOpacity(found.contentBgOpacity);
      }
    } else {
      setCustomBgType('template');
      const defaults = getTemplateDefaults(key);
      setCardWidth(defaults.cardWidth);
      setContentBgOpacity(defaults.contentBgOpacity);
      setTextColor(defaults.textColor);
      if (key === 'photo') {
        setCardWidth(800);
        setContentBgOpacity(0);
      } else if (key === 'classic') {
        setCustomBgType('gradient');
        setCustomBgGradient({ from: '#185491', to: '#a72f6d', angle: 128 });
        setCardWidth(557);
        setContentBgOpacity(53);
      } else {
        setCardWidth(557);
        setContentBgOpacity(53);
      }
    }
  };

  const handleSelectBackgroundImage = (imageUrl) => {
    const image = backgroundImages.find(b => b.url === imageUrl);
    const nextColor = image?.textColor || '#050505';
    setCustomBgType('image');
    setCustomBgImage(imageUrl);
    setContentBgOpacity(0);
    setTextColor(nextColor);
  };

  return (
    <div className="app-layout">
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 9999,
        pointerEvents: 'auto'
      }}>
        <a 
          href="https://chromewebstore.google.com/detail/%E7%94%9F%E6%88%90%E5%8D%A1%E7%89%87/nnlaifaaaehfilcilickkmmcngbdmcfp?utm_source=app-launcher" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            padding: '8px 14px',
            background: 'linear-gradient(135deg, #4b7dff 0%, #3a6fff 100%)',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 600,
            boxShadow: '0 10px 22px rgba(23, 52, 140, 0.28)',
            letterSpacing: '0.3px',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 12px 26px rgba(23, 52, 140, 0.34)';
            e.currentTarget.style.background = 'linear-gradient(135deg, #3f73ff 0%, #3569ff 100%)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 10px 22px rgba(23, 52, 140, 0.28)';
            e.currentTarget.style.background = 'linear-gradient(135deg, #4b7dff 0%, #3a6fff 100%)';
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/chrome-icon.webp`}
            alt=""
            width="18"
            height="18"
            style={{ display: 'block' }}
          />
          Google插件下载
        </a>
      </div>
      <aside className="sidebar sidebar-left">
        <button
          type="button"
          onClick={resetToDefaults}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            margin: '6px 0 18px 6px',
            padding: 0,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer'
          }}
          aria-label="刷新页面"
        >
          <img
            src={`${process.env.PUBLIC_URL}/apple-icon.png`}
            alt=""
            width="40"
            height="40"
            style={{ display: 'block' }}
          />
        </button>
        <TemplateList
          selected={selectedTemplate}
          onSelect={handleSelectTemplate}
          templates={allTemplates}
          onDeleteCustom={handleDeleteCustomTemplate}
          customTemplates={customTemplates}
          bgImages={backgroundImages}
          selectedBgImage={customBgImage}
          onSelectBgImage={handleSelectBackgroundImage}
        />
      </aside>
      <main className="main-content">
        <div className="card-preview">
          <CardEditor
            key={editorResetKey}
            template={selectedTemplate}
            showDate={showDate}
            showAuthor={showAuthor}
            showCount={showCount}
            showAvatar={showAvatar}
            customBgType={customBgType}
            customBgColor={customBgColor}
            customBgGradient={customBgGradient}
            customBgImage={customBgImage}
            cardWidth={cardWidth}
            contentBgOpacity={contentBgOpacity}
            fontFamily={fontFamily}
            textColor={textColor}
            textScale={textScale}
            onSaveTemplate={handleSaveTemplate}
            avatar={avatar}
            onAvatarChange={setAvatar}
            initialContent={initialContent}
          />
        </div>
      </main>
      <aside className="sidebar sidebar-right">
        <div className="sidebar-title">属性</div>
        <PropertyPanel
          showDate={showDate} setShowDate={setShowDate}
          showAuthor={showAuthor} setShowAuthor={setShowAuthor}
          showCount={showCount} setShowCount={setShowCount}
          showAvatar={showAvatar} setShowAvatar={setShowAvatar}
          customBgType={customBgType} setCustomBgType={setCustomBgType}
          customBgColor={customBgColor} setCustomBgColor={setCustomBgColor}
          customBgGradient={customBgGradient} setCustomBgGradient={setCustomBgGradient}
          cardWidth={cardWidth} setCardWidth={setCardWidth}
          contentBgOpacity={contentBgOpacity} setContentBgOpacity={setContentBgOpacity}
          fontFamily={fontFamily} setFontFamily={setFontFamily}
          fontOptions={fontOptions}
          textColor={textColor} setTextColor={setTextColor}
          textScale={textScale} setTextScale={setTextScale}
        />
      </aside>
    </div>
  );
}

export default App;
